<script setup lang="ts">
import { ref, computed, watch, onBeforeMount } from 'vue';
import merge from 'lodash-es/merge';
import Multiselect from 'vue-multiselect/src/Multiselect.vue';
import type { ListItemModel } from '../../../pages/intralearn/modules/events/_runtime/services/EventCategoriesService';
import EventCategoriesService from '../../../pages/intralearn/modules/events/_runtime/services/EventCategoriesService';
import { useWebsitesStore } from '#layers/cms/app/pages/cms/management/websites/_runtime/stores/websites';
import type { CurrentWebsite, Option } from '@/helpers/Interfaces';
import type { FormType } from '@/helpers/Form';

defineOptions({
    name: 'WidgetUpcomingEventsSettings'
});

const props = defineProps<{ modelValue: any }>();
const emit = defineEmits<{ (e: 'update:modelValue', payload: any): void }>();

const { $t } = useLocalization();

interface FormSettings
{
    settings: any;
}

const defaults = ref<any>({
    pages: [],
    display: '3',
    categories: [],
    allCategories: true
});

const categoriesOptions = ref<ListItemModel[]>([]);

const form = ref<FormType<FormSettings>>(props.modelValue ?? ({ settings: {}, $errors: { first: () => null, state: () => null } } as unknown as FormType<FormSettings>));

watch(() => props.modelValue, (val) =>
{
    if (val)
    {
        form.value = val;
    }
});

const current = computed<CurrentWebsite>(() =>
{
    return useWebsitesStore().current;
});

const selectedCategoriesOptions = computed<Option[]>(() =>
{
    return categoriesOptions.value.map((p) =>
    {
        return { value: p.id.toString(), text: p.name };
    });
});

const displayOptions = computed<Option[]>(() =>
{
    return [
        { value: '3', text: $t('[[[3]]]') },
        { value: '5', text: $t('[[[5]]]') },
        { value: '10', text: $t('[[[10]]]') }
    ];
});

function triggerModel(): void
{
    emit('update:modelValue', form.value);
}

watch(() => form.value.settings, () =>
{
    triggerModel();
}, { deep: true });

async function loadCategories(): Promise<void>
{
    try
    {
        const response = await EventCategoriesService.getOptions(form.value.websiteId, form.value.locale);

        categoriesOptions.value = [{
            id: 0,
            name: $t('[[[Wszystkie kategorie]]]'),
            color: null,
            dateCreatedUtc: null,
            dateModifiedUtc: null,
            eventsCount: 0,
            locale: ''
        }].concat(response);
    }
    catch (ex)
    {
        categoriesOptions.value = [];
        $log.debug(ex);
    }
}

onBeforeMount(async (): Promise<void> =>
{
    await wait(() => current.value.website);
    await loadCategories();
    form.value.settings = merge(defaults.value, form.value.settings);
});
</script>

<template>
    <IdeoFormGroup v-if="form && form.settings" :label="$t('[[[Wybór kategorii wydarzeń]]]')" :invalid-feedback="form.$errors.first('settings.categories')" :state="form.$errors.state('settings.categories')">
        <Multiselect
            v-model="form.settings.categories"
            label="text"
            track-by="value"
            :options="selectedCategoriesOptions"
            :multiple="true"
            :placeholder="$t('[[[Wybierz kategorię]]]')"
        >
            <template #noOptions>
                {{ $t('[[[Lista jest pusta]]]') }}
            </template>
            <template #noResult>
                {{ $t('[[[Nie znaleziono żadnych wyników.]]]') }}
            </template>
        </Multiselect>
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Ilość wydarzeń]]]')" :invalid-feedback="form.$errors.first('settings.display')" :state="form.$errors.state('settings.display')" required>
        <IdeoFormSelect v-model="form.settings.display" :options="displayOptions" />
    </IdeoFormGroup>
</template>
